import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useSettings } from "@/contexts/SettingsContext";
import { useLang } from "@/contexts/LangContext";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";

const GROUPS = [
  {
    label: "Contact",
    fields: [
      { key: "phone", label: "Phone", placeholder: "+44 7918 272298" },
      { key: "email", label: "Email (notifications go here)", placeholder: "info@c-carpenter.co.uk" },
      { key: "address_en", label: "Address (EN)" },
      { key: "address_es", label: "Address (ES)" },
      { key: "hours_en", label: "Hours (EN)" },
      { key: "hours_es", label: "Hours (ES)" },
    ],
  },
  {
    label: "Social links",
    fields: [
      { key: "facebook_url", label: "Facebook", placeholder: "https://facebook.com/…" },
      { key: "instagram_url", label: "Instagram", placeholder: "https://instagram.com/…" },
      { key: "youtube_url", label: "YouTube channel", placeholder: "https://youtube.com/@…" },
      { key: "tiktok_url", label: "TikTok" },
      { key: "linkedin_url", label: "LinkedIn" },
      { key: "pinterest_url", label: "Pinterest" },
      { key: "houzz_url", label: "Houzz" },
      { key: "google_business_url", label: "Google Business / Maps" },
    ],
  },
  {
    label: "Featured video",
    fields: [
      { key: "featured_video_id", label: "YouTube video ID (part after v= or /shorts/)", placeholder: "XcK_hnJA1q4" },
      { key: "featured_video_title_en", label: "Video title (EN)" },
      { key: "featured_video_title_es", label: "Video title (ES)" },
    ],
    booleanFields: [
      { key: "featured_video_is_short", label: "Featured video is a YouTube Short (vertical 9:16)" },
    ],
  },
  {
    label: "Home — Hero",
    fields: [
      { key: "hero_title_en", label: "Hero title (EN)", textarea: true, placeholder: "Built by hand.\\nFinished with intention." },
      { key: "hero_title_es", label: "Hero title (ES)", textarea: true, placeholder: "Hecho a mano.\\nTerminado con intención." },
      { key: "hero_subtitle_en", label: "Hero subtitle (EN)", textarea: true },
      { key: "hero_subtitle_es", label: "Hero subtitle (ES)", textarea: true },
    ],
  },
  {
    label: "Home — Studio intro",
    fields: [
      { key: "intro_title_en", label: "Intro title (EN)" },
      { key: "intro_title_es", label: "Intro title (ES)" },
      { key: "intro_body_en", label: "Intro body (EN)", textarea: true },
      { key: "intro_body_es", label: "Intro body (ES)", textarea: true },
    ],
  },
  {
    label: "About page",
    fields: [
      { key: "about_title_en", label: "About title (EN)", textarea: true },
      { key: "about_title_es", label: "About title (ES)", textarea: true },
      { key: "mission_en", label: "Mission (EN)", textarea: true },
      { key: "mission_es", label: "Mission (ES)", textarea: true },
      { key: "vision_en", label: "Vision (EN)", textarea: true },
      { key: "vision_es", label: "Vision (ES)", textarea: true },
    ],
  },
  {
    label: "Footer",
    fields: [
      { key: "footer_tagline_en", label: "Footer tagline (EN)" },
      { key: "footer_tagline_es", label: "Footer tagline (ES)" },
    ],
  },
];

const ALL_STRING_KEYS = GROUPS.flatMap((g) => g.fields.map((f) => f.key));
const ALL_BOOL_KEYS = GROUPS.flatMap((g) => (g.booleanFields || []).map((f) => f.key));

export default function AdminSettings() {
  const { settings, refresh } = useSettings();
  const { t } = useLang();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [openGroups, setOpenGroups] = useState({ Contact: true });

  useEffect(() => {
    setForm(settings || {});
  }, [settings]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {};
      ALL_STRING_KEYS.forEach((k) => (payload[k] = form[k] || ""));
      ALL_BOOL_KEYS.forEach((k) => (payload[k] = !!form[k]));
      await api.put("/admin/settings", payload);
      await refresh();
      toast.success("Settings saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleGroup = (label) =>
    setOpenGroups((s) => ({ ...s, [label]: !s[label] }));

  return (
    <div data-testid="admin-settings">
      <h1 className="font-display text-3xl sm:text-4xl text-ink mb-2">{t("admin.settings")}</h1>
      <p className="text-ink-muted text-sm mb-8">
        Edit everything visible on your website — contact info, social links, hero title, about page. Leave a field empty to keep the default.
      </p>

      <form onSubmit={save} className="space-y-4 max-w-4xl">
        {GROUPS.map((g) => (
          <section
            key={g.label}
            data-testid={`settings-group-${g.label.replace(/\s+/g, "-").toLowerCase()}`}
            className="bg-bg-surface border border-edge"
          >
            <button
              type="button"
              onClick={() => toggleGroup(g.label)}
              className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-bg-hover transition-colors"
            >
              <span className="font-display text-xl text-ink">{g.label}</span>
              <span className="text-eyebrow text-brand">{openGroups[g.label] ? "—" : "+"}</span>
            </button>
            {openGroups[g.label] && (
              <div className="px-5 pb-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                {g.fields.map((f) => (
                  <div key={f.key} className={f.textarea ? "md:col-span-2" : ""}>
                    <label className="text-eyebrow text-ink-muted block mb-2">{f.label}</label>
                    {f.textarea ? (
                      <textarea
                        data-testid={`settings-${f.key}`}
                        value={form[f.key] ?? ""}
                        placeholder={f.placeholder || ""}
                        onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                        rows={3}
                        className="w-full bg-bg border border-edge px-4 py-3 text-ink focus:outline-none focus:border-brand"
                      />
                    ) : (
                      <input
                        data-testid={`settings-${f.key}`}
                        type="text"
                        value={form[f.key] ?? ""}
                        placeholder={f.placeholder || ""}
                        onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                        className="w-full bg-bg border border-edge px-4 py-3 text-ink focus:outline-none focus:border-brand"
                      />
                    )}
                  </div>
                ))}
                {(g.booleanFields || []).map((f) => (
                  <label
                    key={f.key}
                    className="md:col-span-2 flex items-center gap-3 text-ink pt-1"
                  >
                    <input
                      data-testid={`settings-${f.key}`}
                      type="checkbox"
                      checked={!!form[f.key]}
                      onChange={(e) => setForm({ ...form, [f.key]: e.target.checked })}
                      className="accent-brand w-4 h-4"
                    />
                    <span className="text-sm">{f.label}</span>
                  </label>
                ))}
              </div>
            )}
          </section>
        ))}

        <div className="pt-4 sticky bottom-0 bg-bg pb-2">
          <button
            data-testid="settings-save"
            type="submit"
            disabled={saving}
            className="w-full sm:w-auto px-7 py-3 bg-brand text-ink-inverse text-sm hover:bg-brand-hover transition-colors disabled:opacity-60"
          >
            {saving ? "…" : t("admin.save")}
          </button>
        </div>
      </form>

      <ChangePasswordSection />
    </div>
  );
}

function ChangePasswordSection() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    if (next !== confirm) {
      toast.error("The new passwords do not match.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password updated");
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      data-testid="change-password"
      className="mt-10 bg-bg-surface border border-edge max-w-4xl"
    >
      <div className="px-5 py-4 border-b border-edge flex items-center gap-3">
        <KeyRound size={18} className="text-brand" />
        <h2 className="font-display text-xl text-ink">Change password</h2>
      </div>
      <form onSubmit={submit} className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
        <PasswordField
          label="Current password"
          testId="pw-current"
          value={current}
          onChange={setCurrent}
        />
        <PasswordField
          label="New password"
          testId="pw-new"
          value={next}
          onChange={setNext}
        />
        <PasswordField
          label="Confirm new password"
          testId="pw-confirm"
          value={confirm}
          onChange={setConfirm}
        />
        <div className="md:col-span-3">
          <button
            type="submit"
            data-testid="pw-submit"
            disabled={saving || !current || !next || !confirm}
            className="px-6 py-3 bg-brand text-ink-inverse text-sm hover:bg-brand-hover transition-colors disabled:opacity-60"
          >
            {saving ? "Updating…" : "Update password"}
          </button>
          <p className="mt-3 text-xs text-ink-dim">
            Minimum 8 characters. Store your new password somewhere safe — we can&apos;t retrieve it.
          </p>
        </div>
      </form>
    </section>
  );
}

function PasswordField({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="text-eyebrow text-ink-muted block mb-2">{label}</label>
      <input
        data-testid={testId}
        type="password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete="new-password"
        className="w-full bg-bg border border-edge px-4 py-3 text-ink focus:outline-none focus:border-brand"
      />
    </div>
  );
}
